<template>
  <Dialog
    :visible="visible"
    :modal="true"
    :closable="false"
    :header="`Generate Snippet Widget Tag for ${selectedSeller.name}`"
    :style="{ width: '40rem', maxWidth: '92vw' }"
  >
    <div class="grid">
      <div class="col-12 sm:col-6">
        <div class="mb-1">Theme</div>
        <SelectButton
          v-model="widgetOptions.theme"
          :options="themeOptions"
          option-label="label"
          option-value="value"
        />
      </div>
      <div class="col-12 sm:col-6">
        <div>
          <div class="mr-2">Hide Progress Bar</div>
          <InputSwitch v-model="widgetOptions.hide_progress_bar" />
        </div>
      </div>
      <div class="col-12 sm:col-6">
        <div class="mb-1">Fab Placement</div>
        <div class="flex gap-3">
          <div class="flex align-items-center gap-1">
            <RadioButton
              v-model="widgetOptions.fab_placement"
              inputId="fab_placement_left"
              value="left"
            />
            <label for="fab_placement_left">Left</label>
          </div>
          <div class="flex align-items-center gap-1">
            <RadioButton
              v-model="widgetOptions.fab_placement"
              inputId="fab_placement_right"
              value="right"
            />
            <label for="fab_placement_right">Right</label>
          </div>
        </div>
      </div>
      <div class="col-12 sm:col-6">
        <div>
          <div class="mr-2">Hide Fab</div>
          <InputSwitch v-model="widgetOptions.hide_fab" />
        </div>
      </div>
      <div class="col-12">
        <div class="mr-2">Use Colors From Cover Image</div>
        <InputSwitch v-model="widgetOptions.use_colors_from_cover_image" />
      </div>
      <template v-if="!widgetOptions.use_colors_from_cover_image">
        <div class="col-12">
          <small class="text-color-secondary">
            Set colors for the whole widget instead of colors based on
            each snippet's cover image.
          </small>
        </div>
        <div class="col-12 sm:col-6">
          <div class="mb-1">Gradient Color</div>
          <InputText
            v-model="widgetOptions.gradient_color"
            placeholder="e.g. #1dddd9, rgb(...), red"
            class="w-full"
          />
        </div>
        <div class="col-12 sm:col-6">
          <div class="mb-1">Primary Button Color</div>
          <InputText
            v-model="widgetOptions.primary_button_color"
            placeholder="e.g. #1dddd9, rgb(...), green"
            class="w-full"
          />
        </div>
        <div class="col-12 sm:col-6">
          <div class="mb-1">Active Word Color</div>
          <InputText
            v-model="widgetOptions.active_word_color"
            placeholder="e.g. #1dddd9, rgb(...), red"
            class="w-full"
          />
        </div>
      </template>
    </div>
    <div class="mt-3">
      <div class="mb-1">
        Copy & paste this code to where you want to embed the snippet widget.
      </div>
      <div
        class="bg-primary-reverse overflow-x-hidden border-1 border-round-md p-3 mb-2 relative"
      >
        <Button
          @click="codeToClipboard"
          icon="pi pi-copy"
          text
          aria-label="Copy"
          v-tooltip.bottom="'Copy'"
          class="absolute right-0 top-0 embed-code-copy-btn"
        />
        <pre ref="componentcodeElement" class="my-0">{{ componentCode }}</pre>
      </div>
    </div>
    <template #footer>
      <Button label="Close" class="mt-3" outlined @click="closeDialog" />
    </template>
  </Dialog>
</template>

<script setup>
import { ref, computed, watch } from "vue";
import Dialog from "primevue/dialog";
import Button from "primevue/button";
import InputText from "primevue/inputtext";
import InputSwitch from "primevue/inputswitch";
import SelectButton from "primevue/selectbutton";
import RadioButton from "primevue/radiobutton";

const props = defineProps({
  visible: {
    type: Boolean,
    default: false,
  },
  selectedSeller: {
    type: Object,
    default: () => ({}),
  },
  partner: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(["close-dialog"]);

const themeOptions = [
  { label: "Light", value: "light" },
  { label: "Dark", value: "dark" },
];

const widgetOptions = ref({
  theme: "light",
  hide_progress_bar: false,
  hide_fab: false,
  fab_placement: "right",
  use_colors_from_cover_image: true,
  gradient_color: "",
  primary_button_color: "",
  active_word_color: "",
});

const componentcodeElement = ref(null);

const componentCode = computed(() => {
  const opts = widgetOptions.value;
  const attrs = [
    `seller="${props.partner.id}"`,
    `subseller="${props.selectedSeller.id}"`,
    `domain="${props.selectedSeller.domain}"`,
  ];

  if (opts.theme === "light" || opts.theme === "dark") {
    attrs.push(`theme="${opts.theme}"`);
  }
  if (opts.hide_progress_bar) {
    attrs.push("hide-progress-bar");
  }
  if (opts.hide_fab) {
    attrs.push("hide-fab");
  }
  if (opts.fab_placement === "left" || opts.fab_placement === "right") {
    attrs.push(`fab-placement="${opts.fab_placement}"`);
  }
  if (!opts.use_colors_from_cover_image) {
    if (opts.gradient_color?.length) {
      attrs.push(`gradient-color="${opts.gradient_color}"`);
    }
    if (opts.primary_button_color?.length) {
      attrs.push(`primary-button-color="${opts.primary_button_color}"`);
    }
    if (opts.active_word_color?.length) {
      attrs.push(`active-word-color="${opts.active_word_color}"`);
    }
  }

  return `<jamx-widget\n  ${attrs.join(
    "\n  "
  )}\n></jamx-widget>\n\n<script src="https://player.jamx.ai/snippet/snippet-widget.js"><\/script>`;
});

watch(
  () => props.visible,
  (newValue) => {
    if (!newValue) {
      widgetOptions.value = {
        theme: "light",
        hide_progress_bar: false,
        hide_fab: false,
        fab_placement: "right",
        use_colors_from_cover_image: true,
        gradient_color: "",
        primary_button_color: "",
        active_word_color: "",
      };
    }
  }
);

function closeDialog() {
  emit("close-dialog");
}

function codeToClipboard() {
  navigator.clipboard.writeText(componentcodeElement.value.innerText);
}
</script>

<style>
.embed-code-copy-btn:focus {
  box-shadow: none !important;
}
</style>
